import { TemplateKey } from "PFApp/constants/templates";
import { Dispatch } from "PFApp/search/search_context";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringify";
import { NormalizedKeyword } from "PFReducers/search_reducer/helpers/normalize_keywords";
import { SEARCH_ACTIONS, SEARCH_ASYNC_ACTIONS } from "PFReducers/search_reducer/search_reducer";
import { Activity, Filters, MyAccountInfo, OrderMeta, Value } from "PFTypes";
import { useEffect, useMemo } from "react";

import { getAPIFilters } from "../../use_filtered_collection";
import { useDebouncedMergeState } from "../helpers/use_debounced_merge_state";

type MatchesSyncArgs = {
  dispatch: Dispatch;
  term: string;
  trimmedTerm: string;
  triggerCounter: number;
  myAccountInfo: MyAccountInfo;
  choosenKeywords: NormalizedKeyword[];
  deletedKeywords: NormalizedKeyword[];
  searchActivity?: Activity;
  searchActivityId?: number;
  searchActivityFetchCounter: number;
  matchesPage: number;
  matchesRequestFilters: Filters<Value> | null;
  matchesRequestOrder: Partial<OrderMeta>;
  collectionKind: "matches" | "profiles";
  prevMatchesFetch?: boolean;
};

export const useMatchesSync = ({
  dispatch,
  term,
  trimmedTerm,
  triggerCounter,
  myAccountInfo,
  choosenKeywords,
  deletedKeywords,
  searchActivity,
  searchActivityId,
  searchActivityFetchCounter,
  matchesPage,
  matchesRequestFilters,
  matchesRequestOrder,
  collectionKind,
  prevMatchesFetch
}: MatchesSyncArgs) => {
  const stringify = useDeterministicStringify();
  const debouncedGoToPage1Dispatch = useDebouncedMergeState(dispatch);

  const matchesRequestAPIFilters = useMemo(
    () => getAPIFilters(matchesRequestFilters ?? undefined) || {},
    [matchesRequestFilters]
  );

  const matchesParamsHash = stringify([matchesRequestAPIFilters, matchesRequestOrder]);
  const choosenKeywordsHash = choosenKeywords
    .map(({ id, importance }) => `${id}:${importance}`)
    .sort()
    .join(",");
  const activityTemplateId = myAccountInfo.templates?.find(({ key }) => key === TemplateKey.Task)?.id;
  const fetchMatchesDebounced = useDebounce(
    () =>
      dispatch({
        type: SEARCH_ASYNC_ACTIONS.SEARCH_TRIGGER_MATCHES_FETCH,
        payload: {
          term,
          searchActivity,
          searchActivityId,
          choosenKeywords,
          deletedKeywords,
          matchesPage,
          matchesRequestOrder,
          matchesRequestFilters: matchesRequestAPIFilters,
          collectionKind,
          prevMatchesFetch,
          searchActivityFetchCounter,
          activityTemplateId
        }
      }),
    500
  );

  useEffect(() => {
    debouncedGoToPage1Dispatch({ matchesPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choosenKeywordsHash, trimmedTerm, matchesParamsHash]);
  useEffect(() => {
    dispatch({ type: SEARCH_ACTIONS.SEARCH_ABORT_MATCHES });
    fetchMatchesDebounced();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedTerm, choosenKeywordsHash, matchesPage, matchesParamsHash, triggerCounter]);
};
